import * as React from "react";
import "../../assets/crudStyles.css";
import "./styles.css";
import { TextInputCounter } from "../../components/TextInputCounter/TextInputCounter";
import {
  required,
  ImageInput,
  Toolbar,
  SaveButton,
  useNotify,
  ImageField,
  useRecordContext,
  useEditController,
  maxLength,
  TabbedForm,
  FormTab,
} from "react-admin";
import useFileUpload from "../../hooks/useFileUpload";
import { ResourceEditor } from "../../components/ResourceEditor/ResourceEditor";

import { CreateScreen as DevWithUsDevStoriesCreate } from "../../components/develop-with-us-page-dev-stories/create";
import { EditScreen as DevWithUsDevStoriesEdit } from "../../components/develop-with-us-page-dev-stories/edit";

import { CreateScreen as DevWithUsPartnerStoriesCreate } from "../../components/develop-with-us-page-partner-stories/create";
import { EditScreen as DevWithUPartnerStoriesEdit } from "../../components/develop-with-us-page-partner-stories/edit";
import { RaEditWrapper } from "../../components/RaEditWrapper";

interface IEdit {
  [key: string]: any;
}

const EditToolbar = ({ files, ...props }: any) => {
  return (
    <Toolbar {...props}>
      <SaveButton
        transform={({ ...data }: IEdit) => {
          const mappedRequest: any = {
            ...data,
          };

          const resourceFilesReq = {
            topSectionImageUploadId:
              files.topSectionImageUpload ?? data.topSectionImageUpload.id,
            topSubsection1ImageUploadId:
              files.topSubsection1ImageUpload ??
              data.topSubsection1ImageUpload.id,
            topSubsection2ImageUploadId:
              files.topSubsection2ImageUpload ??
              data.topSubsection2ImageUpload.id,
          };

          // Clean up fields.
          delete mappedRequest.topSectionImageUpload;
          delete mappedRequest.topSubsection1ImageUpload;
          delete mappedRequest.topSubsection2ImageUpload;
          delete mappedRequest.order;
          delete mappedRequest.id;
          delete mappedRequest.enabled;

          return {
            ...mappedRequest,
            ...resourceFilesReq,
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const EditScreen = (props: any) => {
  const notify = useNotify();

  // Upload Files
  const [resourceFiles, setResourceFiles] = React.useState<{
    topSectionImageUpload: null | any;
    topSubsection1ImageUpload: null | any;
    topSubsection2ImageUpload: null | any;
  }>({
    topSectionImageUpload: null,
    topSubsection1ImageUpload: null,
    topSubsection2ImageUpload: null,
  });

  const [resourceImgs, setResourceImgs] = React.useState<{
    [key: string]: any;
  }>({});

  const renderImgPreview = (name: string) => {
    if (!resourceImgs[name]) {
      return <ImageField source="uri" title="info.original_filename" />;
    }

    return (
      <FileFieldWithContext
        placeholder={
          <div className="MuiBox-root css-cb34tx-RaImageField-root">
            <img
              src={resourceImgs[name].uri}
              title={resourceImgs[name].title}
              width={200}
              height={200}
              className="RaImageField-image"
            />
          </div>
        }
      >
        <ImageField source="uri" title="title" />
      </FileFieldWithContext>
    );
  };

  // Used to track the uploaded files and show in progress or error.
  const [uploadedFileNames, setUploadFileNames] = React.useState<{
    [key: string]: string | boolean;
  }>({});

  const { uploadFile } = useFileUpload();
  const handlePrefileUpload = async (name: string, files: File[]) => {
    const fileName = files[0].name;
    notify(`Uploading file... ${fileName}`);

    setUploadFileNames({ ...uploadedFileNames, [fileName]: "Uploading..." });

    const asset = await uploadFile(files[0], name);
    if (asset !== undefined) {
      setResourceImgs({
        ...resourceImgs,
        [name]: {
          title: asset.info.original_filename,
          uri: asset.uri,
          uploading: false,
        },
      });

      const newValue = asset.id;
      setResourceFiles({ ...resourceFiles, [name]: newValue });
      notify(`Upload completed! File: ${fileName}`);
      setUploadFileNames({ ...uploadedFileNames, [fileName]: true });
    } else {
      notify(`Error uploading file! ${fileName}`);
      setUploadFileNames({
        ...uploadedFileNames,
        [fileName]: "Error uploading file, remove and try again.",
      });
    }
  };

  const FileFieldWithContext = (props: any) => {
    const { title, uri } = useRecordContext();

    if (uri) {
      return props.children;
    }

    if (!Object.hasOwn(uploadedFileNames, title)) {
      return null;
    }

    if (uploadedFileNames[title] === true) {
      return props.placeholder;
    } else {
      return <p>{uploadedFileNames[title]}</p>;
    }
  };

  // Record handler.
  const { record } = useEditController(props);

  if (record) {
    record.enabled = record.publishedAt !== null ? true : false;
  }

  return (
    <RaEditWrapper mutationMode="pessimistic">
      <TabbedForm
        toolbar={<EditToolbar files={resourceFiles} record={record} />}
      >
        <FormTab label="Top">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSectionTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="topSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSectionImageUpload"
              label="Top Section Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSectionImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSectionImageUpload")}
            </ImageInput>
          </div>

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection1Title"
            label="Section #1 Title"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection1Description"
            label="Section #1 Description"
            multiline
            fullWidth
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSubsection1ImageUpload"
              label="Section #1 Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSubsection1ImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSubsection1ImageUpload")}
            </ImageInput>
          </div>

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection2Title"
            label="Section #2 Title"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            formClassName="form-text-input-field"
            source="topSubsection2Description"
            label="Section #2 Description"
            multiline
            fullWidth
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <div className="resource-icons-grid">
            <ImageInput
              source="topSubsection2ImageUpload"
              label="Section #2 Image (.png, .jpg, .jpeg)"
              validate={[required()]}
              accept={"image/png,image/jpg,image/jpeg"}
              options={{
                onDropAccepted: handlePrefileUpload.bind(
                  null,
                  "topSubsection2ImageUpload",
                ),
              }}
            >
              {renderImgPreview("topSubsection2ImageUpload")}
            </ImageInput>
          </div>
        </FormTab>

        <FormTab label="Dev Stories">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="developmentStoriesSectionTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="developmentStoriesSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <ResourceEditor
            resourceId={"develop-with-us-page-dev-stories"}
            createNewLabel="Add New Dev Story"
            updatedOrderMsg="Story order updated."
            updatingOrderMsg="Updating story order..."
            heading="Stories"
            renderName={(resource: any) =>
              `${resource.title.substr(0, 100)}...`
            }
            CreateScreen={DevWithUsDevStoriesCreate}
            EditScreen={DevWithUsDevStoriesEdit}
            hasOrder={false}
            hasEnabled={false}
          />
        </FormTab>

        <FormTab label="Partner Stories">
          <TextInputCounter
            formClassName="form-text-input-field"
            source="partnershipStoriesSectionTitle"
            multiline
            fullWidth
            validate={[required(), maxLength(100)]}
            limit={100}
          />

          <TextInputCounter
            source="partnershipStoriesSectionDescription"
            multiline
            fullWidth
            resettable
            validate={[required(), maxLength(500)]}
            limit={500}
          />

          <ResourceEditor
            resourceId={"develop-with-us-page-partner-stories"}
            createNewLabel="Add New Partner Story"
            updatedOrderMsg="Story order updated."
            updatingOrderMsg="Updating story order..."
            heading="Partner Stories"
            renderName={(resource: any) =>
              `${resource.title.substr(0, 100)}...`
            }
            CreateScreen={DevWithUsPartnerStoriesCreate}
            EditScreen={DevWithUPartnerStoriesEdit}
            hasOrder={false}
            hasEnabled={false}
          />
        </FormTab>
      </TabbedForm>
    </RaEditWrapper>
  );
};
